import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import ItalyMap from "../components/ItalyMap";
import ScadenzePanel from "../components/ScadenzePanel";
import ChecklistProgress from "../components/ChecklistProgress";
import F24UnpaidList from "../components/F24UnpaidList";
import ScheduledToday from "../components/ScheduledToday";
import ReviewsPanel from "../components/ReviewsPanel";
import ConfirmationsPanel from "../components/ConfirmationsPanel";
import { MilestoneTrack } from "../components/MilestoneTrack";
import { Donut, dueStats } from "./Contabilita";

const EUR = (n) => "€ " + Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function ContabilitaGlance() {
  const [board, setBoard] = useState(null);
  useEffect(() => {
    api.get(`/contabilita/board?year=${new Date().getFullYear()}`).then((r) => setBoard(r.data)).catch(() => {});
  }, []);
  if (!board) return null;
  const regimi = ["forfettario", "semplificato", "ordinario"];
  const perRegime = regimi.map((r) => {
    const clients = board.clients.filter((c) => c.regime === r);
    const agg = clients.reduce((a, c) => {
      // stessi conteggi della pagina Contabilità: esclude gli adempimenti "non previsti" per cliente
      const excl = c.excluded || [];
      const monthly = (board.tasks[r].monthly || []).filter((t) => !excl.includes(t.key));
      const annual = (board.tasks[r].annual || []).filter((t) => !excl.includes(t.key));
      const ds = dueStats(c, board.year, monthly, annual);
      return { due: a.due + ds.due, done: a.done + ds.done };
    }, { due: 0, done: 0 });
    return { key: r, label: board.tasks[r].label, clients: clients.length, ...agg };
  });
  const tot = perRegime.reduce((a, x) => ({ due: a.due + x.due, done: a.done + x.done }), { due: 0, done: 0 });
  const assigned = board.clients.filter((c) => c.regime).length;
  if (assigned === 0) return null;
  const ratio = tot.due > 0 ? tot.done / tot.due : 1;
  return (
    <div className="panel" data-testid="contab-glance" style={{ marginBottom: 8 }}>
      <div className="toolbar" style={{ gap: 18, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Donut ratio={ratio} size={56} testid="glance-donut-total" />
          <div>
            <div className="today-head" style={{ marginBottom: 2 }}>📒 Situazione registrazioni</div>
            <b style={{ fontSize: 18 }} data-testid="glance-total-pct">{Math.round(ratio * 100)}%</b>
            <span className="muted"> aggiornato · {tot.done}/{tot.due} adempimenti dovuti fino al mese scorso</span>
          </div>
        </div>
        <div className="spacer" />
        {perRegime.map((r) => (
          <div key={r.key} style={{ display: "flex", alignItems: "center", gap: 8 }} data-testid={`glance-${r.key}`}>
            {r.due > 0 ? <Donut ratio={r.done / r.due} size={36} /> : <span className="muted" style={{ width: 36, textAlign: "center", fontSize: 16 }}>—</span>}
            <div style={{ fontSize: 12 }}>
              <b>{r.label}</b>
              <div className="muted">{r.clients} clienti · {r.due > 0 ? `${Math.round((r.done / r.due) * 100)}%` : r.key === "forfettario" ? "rileva a fine anno" : "—"}</div>
            </div>
          </div>
        ))}
        <Link className="today-link" to="/contabilita" style={{ alignSelf: "center" }}>Apri registrazioni →</Link>
      </div>
    </div>
  );
}
const MODE_SHORT = { zoom: "Zoom", phone: "Telefono", presenza: "In studio", trasferta: "Trasferta", studio: "Studio", scelta: "A scelta" };

const IC = {
  person: <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2M12 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0-8 0" />,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
  spark: <path d="M12 3v3m0 12v3m9-9h-3M6 12H3m13.5-6.5-2 2m-7 7-2 2m11 0-2-2m-7-7-2-2" />,
  coin: <><circle cx="12" cy="12" r="9" /><path d="M14.5 9.5a3 3 0 0 0-2.5-1.5c-1.5 0-2.5 1-2.5 2s1 2 2.5 2 2.5 1 2.5 2-1 2-2.5 2a3 3 0 0 1-2.5-1.5M12 6.5v11" /></>,
  approve: <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="m9 11 3 3L22 4" /></>,
  receipt: <><path d="M4 3h16v18l-3-2-3 2-3-2-3 2z" /><path d="M8 8h8M8 12h8M8 16h5" /></>,
  target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></>,
  book: <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V3H6.5A2.5 2.5 0 0 0 4 5.5z" /><path d="M8 7h8M8 11h6" /></>,
};
function OrbIcon({ k }) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{IC[k]}</svg>;
}
function Orb({ pad = 0, alert, ico, num, lbl, to, tint }) {
  const style = tint ? {
    borderColor: tint,
    boxShadow: `0 0 0 4px ${tint}1f, 0 0 40px ${tint}8c, inset 0 0 24px ${tint}3d`,
  } : undefined;
  return (
    <div className={"orb-wrap" + (alert ? " orb-alert" : "")} style={{ paddingTop: pad }}>
      <Link className="orb" to={to} style={style}>
        <OrbIcon k={ico} />
        <div className="orb-num" style={tint ? { color: tint, textShadow: `0 0 10px ${tint}cc` } : undefined}>{num}</div>
        <div className="orb-lbl">{lbl}</div>
      </Link>
    </div>
  );
}

// Semicerchio ampio e simmetrico: i pallini salgono verso il centro e ridiscendono
const ARC_PADS = [96, 54, 20, 0, 20, 54, 96];

export default function Dashboard() {
  const [stats, setStats] = useState({
    active_clients: 0, leads: 0, persons: 0, tags: 0, da_gestire: 0,
    ltv_total: 0, avg_monthly_margin: 0, geo_distribution: [],
  });
  const [today, setToday] = useState({ appointments: [], tasks: [], messages: [], messages_count: 0, tasks_overdue: 0 });
  const [obj, setObj] = useState(null);
  const [proj, setProj] = useState(null);
  const [lossClients, setLossClients] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [f24Unpaid, setF24Unpaid] = useState(0);
  const [cov, setCov] = useState(null);
  const [covBusy, setCovBusy] = useState(false);
  const [leadsInCorso, setLeadsInCorso] = useState(0);
  const [regRatio, setRegRatio] = useState(null);
  const [f24Next, setF24Next] = useState(null);
  const [msum, setMsum] = useState(null);
  const [canali, setCanali] = useState(null);
  const [andamento, setAndamento] = useState(null);

  const recheckCoverage = async () => {
    setCovBusy(true);
    try { const r = await api.post("/emails/coverage-check"); setCov(r.data); }
    catch (e) { /* ignora */ }
    finally { setCovBusy(false); }
  };

  const [covImportMsg, setCovImportMsg] = useState("");
  const importMissing = async () => {
    setCovBusy(true); setCovImportMsg("Import avviato…");
    try {
      await api.post("/emails/coverage-import");
      const t = setInterval(async () => {
        try {
          const s = (await api.get("/emails/coverage-import/status", { noCache: true })).data;
          if (s.running) { setCovImportMsg(`Import in corso… ${s.importate || 0} importate`); return; }
          clearInterval(t);
          setCovImportMsg(s.error ? `Errore: ${s.error}` : `Importate ${s.importate || 0} email${s.ancora_mancanti ? ` · ancora mancanti: ${s.ancora_mancanti}` : " · tutto allineato ✓"}`);
          const c = await api.get("/emails/coverage-status", { noCache: true });
          setCov(c.data);
          setCovBusy(false);
        } catch { clearInterval(t); setCovBusy(false); }
      }, 3000);
    } catch (e) { setCovImportMsg("Errore: " + (e.response?.data?.detail || e.message)); setCovBusy(false); }
  };

  useEffect(() => {
    api.get("/stats/dashboard").then((r) => setStats(r.data)).catch(() => {});
    api.get("/stats/today").then((r) => setToday(r.data)).catch(() => {});
    api.get("/invoices/objective").then((r) => setObj(r.data)).catch(() => {});
    api.get("/invoices/projection").then((r) => setProj(r.data)).catch(() => {});
    api.get("/approvals/count").then((r) => setPendingApprovals(r.data.pending || 0)).catch(() => {});
    api.get("/f24/unpaid-count").then((r) => setF24Unpaid(r.data.unpaid || 0)).catch(() => {});
    api.get("/emails/coverage-status").then((r) => setCov(r.data)).catch(() => {});
    api.get("/pipeline/stats").then((r) => setLeadsInCorso(r.data.in_corso || 0)).catch(() => {});
    api.get("/f24/upcoming-total").then((r) => setF24Next(r.data)).catch(() => {});
    api.get("/invoices/margin-summary").then((r) => setMsum(r.data)).catch(() => {});
    api.get("/stats/canali").then((r) => setCanali(r.data)).catch(() => {});
    api.get("/stats/canali/andamento?months=6").then((r) => setAndamento(r.data)).catch(() => {});
    api.get(`/contabilita/board?year=${new Date().getFullYear()}`).then((r) => {
      const b = r.data;
      const agg = (b.clients || []).filter((c) => ["forfettario", "semplificato", "ordinario"].includes(c.regime))
        .reduce((a, c) => {
          const ds = dueStats(c, b.year, (b.tasks[c.regime]?.monthly || []).filter((t) => !(c.excluded || []).includes(t.key)), (b.tasks[c.regime]?.annual || []).filter((t) => !(c.excluded || []).includes(t.key)));
          return { due: a.due + ds.due, done: a.done + ds.done };
        }, { due: 0, done: 0 });
      setRegRatio(agg.due > 0 ? agg.done / agg.due : null);
    }).catch(() => {});
    api.get("/companies?limit=500").then((r) => {
      const rows = (r.data || [])
        .filter((c) => (c.annual_margin || 0) < 0 && ((c.emails_used || 0) + (c.tasks_used || 0)) > 0)
        .filter((c) => !/sc\s*dottori|dottori commercialisti/i.test(c.name || ""))
        .sort((a, b) => (a.annual_margin || 0) - (b.annual_margin || 0));
      setLossClients(rows);
    }).catch(() => {});
  }, []);

  const oggi = new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
  const regColor = regRatio == null ? "#94a3b8" : regRatio >= 0.999 ? "#39ff14" : regRatio >= 0.7 ? "#a3e635" : regRatio >= 0.4 ? "#facc15" : "#fb7185";

  return (
    <div className="dash-flex">
      <div className="hero m-o1">
        <div className="hero-orbit">
          <Orb pad={ARC_PADS[0]} ico="person" num={<>{stats.active_clients} <span style={{ opacity: .5 }}>·</span> {stats.leads}</>} lbl="Clienti · Lead" to="/aziende" />
          <Orb pad={ARC_PADS[1]} ico="clock" num={today.appointments.length + (today.gcal_events || []).length} lbl="Agenda" to="/calendario" />
          <Orb pad={ARC_PADS[2]} ico="mail" num={stats.da_gestire} lbl="Da gestire" to="/da-gestire" />
          <Orb pad={ARC_PADS[3]} ico="book" num={regRatio == null ? "—" : `${Math.round(regRatio * 100)}%`} lbl="Registrazioni" to="/contabilita" tint={regColor} />
          <Orb pad={ARC_PADS[4]} alert={pendingApprovals > 0} ico="approve" num={pendingApprovals} lbl="Da approvare" to="/da-approvare" />
          <Orb pad={ARC_PADS[5]} ico="target" num={leadsInCorso} lbl="Lead in gestione" to="/lead" />
          <Orb pad={ARC_PADS[6]} alert={f24Unpaid > 0} ico="receipt" num={f24Unpaid} lbl="F24 non pagati" to="/scadenze-f24" />
        </div>
        <div className="hero-title">SILVIA CASALI</div>
        <div className="hero-sub">Il Commercialista come partner strategico</div>
      </div>

      {cov && cov.missing_count > 0 && (
        <div className="panel" style={{ borderColor: "var(--danger)", boxShadow: "var(--halo-alert)" }} data-testid="coverage-alert">
          <div className="today-head">⚠️ Controllo mail: <span className="nav-badge" style={{ background: "var(--danger)" }}>{cov.missing_count}</span> email presenti su Gmail non risultano nel CRM (ultimi {cov.window_days} giorni)</div>
          <ul className="today-list">
            {(cov.missing || []).slice(0, 5).map((m) => (
              <li key={m.gmail_id}><strong>{m.from_name || m.from_email}</strong><span className="muted"> · {m.subject || "(senza oggetto)"} · {m.account}</span></li>
            ))}
          </ul>
          <p className="muted" style={{ fontSize: 12.5 }}>La sincronizzazione automatica le importerà a breve. Se il numero non scende, usa «Importa adesso» o controlla il collegamento Gmail in Impostazioni.</p>
          <div className="toolbar" style={{ gap: 8 }}>
            <button className="btn sm" onClick={recheckCoverage} disabled={covBusy} data-testid="coverage-recheck-btn">{covBusy ? "Controllo…" : "🔄 Ricontrolla adesso"}</button>
            <button className="btn sm primary" onClick={importMissing} disabled={covBusy} data-testid="coverage-import-btn">{covBusy ? "…" : "⬇️ Importa adesso nel CRM"}</button>
            {covImportMsg && <span className="muted" style={{ fontSize: 12.5 }} data-testid="coverage-import-msg">{covImportMsg}</span>}
          </div>
        </div>
      )}

      <h3 style={{ marginTop: 4 }} className="m-o1">Programma di oggi — <span style={{ textTransform: "capitalize" }}>{oggi}</span></h3>
      {canali && (
        <div className="panel m-o6" data-testid="dash-canali" style={{ padding: "14px 18px" }}>
          <div className="today-head" style={{ marginBottom: 8 }}>📣 Lead per canale — {canali.mese} <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>· conversione = quanti sono diventati clienti (sullo storico)</span></div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {canali.canali.map((c) => (
              <div key={c.nome} className="dash-card" data-testid={`canale-${c.nome}`} style={{ flex: "1 1 150px", padding: "10px 14px" }}>
                <div className="num" style={{ fontSize: 20 }}>{c.icona} {c.mese}</div>
                <div className="lbl">{c.nome} · nel mese</div>
                <div style={{ fontSize: 12, marginTop: 4 }} className="muted">
                  {c.totale} totali{c.conversione_pct != null
                    ? <> · <span style={{ color: c.conversione_pct >= 10 ? "#39ff14" : "#facc15", fontWeight: 700 }}>{c.clienti} clienti ({c.conversione_pct}%)</span></>
                    : ""}
                </div>
              </div>
            ))}
          </div>
          {andamento && (() => {
            const max = Math.max(1, ...andamento.serie.flatMap((s) => s.valori));
            return (
              <div style={{ marginTop: 16 }} data-testid="canali-andamento">
                <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>Andamento ultimi 6 mesi</div>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
                  {andamento.mesi.map((mese, i) => (
                    <div key={mese} style={{ flex: 1, textAlign: "center" }}>
                      <div style={{ display: "flex", gap: 3, alignItems: "flex-end", justifyContent: "center", height: 80 }}>
                        {andamento.serie.map((s) => (
                          <div key={s.nome} title={`${s.icona} ${s.nome}: ${s.valori[i]}`}
                               style={{ width: 10, borderRadius: "3px 3px 0 0", background: s.colore,
                                        height: `${Math.round((s.valori[i] / max) * 76) + 4}px`,
                                        boxShadow: `0 0 8px ${s.colore}66`, opacity: s.valori[i] ? 1 : 0.22 }} />
                        ))}
                      </div>
                      <div className="muted" style={{ fontSize: 10.5, marginTop: 5 }}>{mese}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8, justifyContent: "center" }}>
                  {andamento.serie.map((s) => (
                    <span key={s.nome} className="muted" style={{ fontSize: 11 }}>
                      <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: s.colore, marginRight: 4 }} />{s.icona} {s.nome}
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}
      <div className="m-o7"><ContabilitaGlance /></div>
      <div className="today-grid">
        <div className="panel today-col m-o2">
          <div className="today-head">📅 Impegni del giorno <span className="nav-badge">{today.appointments.length + (today.gcal_events || []).length}</span></div>
          {today.appointments.length + (today.gcal_events || []).length === 0 ? <p className="muted">Nessun impegno in agenda oggi.</p> : (
            <ul className="today-list">
              {today.appointments.map((a) => (
                <li key={a.id}>
                  <strong>{a.time}</strong> · {a.company || a.name || "—"}
                  <span className="muted"> · {MODE_SHORT[a.mode] || a.mode}{a.status === "in_attesa" ? " (da accettare)" : ""}</span>
                </li>
              ))}
              {(today.gcal_events || []).map((e, i) => (
                <li key={`g${i}`} data-testid={`today-gcal-${i}`}>
                  <strong>{e.time}</strong> · {e.summary}
                  <span style={{ color: e.color || "#22a35a", fontSize: 12 }}> · 🗓 Google{e.calendar_name ? ` (${e.calendar_name})` : ""}</span>
                </li>
              ))}
            </ul>
          )}
          <Link className="today-link" to="/calendario">Vai al calendario →</Link>
        </div>

        <div className="panel today-col m-o3">
          <div className="today-head">✅ Incarichi del giorno <span className="nav-badge">{today.tasks.length}</span>{today.tasks_overdue > 0 && <span className="nav-badge" style={{ background: "var(--danger)" }}>{today.tasks_overdue} in ritardo</span>}</div>
          {today.tasks.length === 0 ? <p className="muted">Nessun incarico in scadenza.</p> : (
            <ul className="today-list">
              {today.tasks.map((t) => (
                <li key={t.id}>
                  {t.overdue && <span style={{ color: "var(--danger)" }}>⚠ </span>}{t.title}
                </li>
              ))}
            </ul>
          )}
          <Link className="today-link" to="/incarichi">Vai agli incarichi →</Link>
        </div>

        <div className="panel today-col m-o4">
          <div className="today-head">✉️ Mail / WhatsApp da clienti e lead <span className="nav-badge">{today.messages_count}</span></div>
          {today.messages.length === 0 ? <p className="muted">Nessun messaggio da clienti o lead in sospeso.</p> : (
            <ul className="today-list">
              {today.messages.map((e) => (
                <li key={e.id}>
                  <Link to={`/email/${e.id}`}><strong>{e.from_name || e.from_email}</strong></Link>
                  <span className="muted"> · {e.subject || "(senza oggetto)"}</span>
                </li>
              ))}
            </ul>
          )}
          <Link className="today-link" to="/da-gestire">Vai a Da gestire →</Link>
        </div>
      </div>

      <div className="m-o5"><ScadenzePanel /><ScheduledToday /><ChecklistProgress /><F24UnpaidList /></div>

      <ReviewsPanel />
      <ConfirmationsPanel />

      <div className="dash-cards">
        <div className="dash-card" data-testid="f24-upcoming-card"><div className="num" style={{ fontSize: 22 }}>{f24Next ? EUR(f24Next.total) : "—"}</div><div className="lbl">F24 da versare nei prossimi 30 giorni{f24Next && f24Next.count ? ` · ${f24Next.count} deleghe` : ""}</div></div>
        <div className="dash-card" data-testid="monthly-margin-card"><div className="num" style={{ fontSize: 22 }}>{msum ? EUR(msum.monthly_margin) : "…"}</div><div className="lbl">Margine mensile dello studio{msum ? ` · media su ${msum.months_elapsed} mesi ${msum.year}` : ""}</div></div>
        <div className="dash-card" data-testid="monthly-margin-per-client-card"><div className="num" style={{ fontSize: 22 }}>{msum ? EUR(msum.monthly_margin_per_client) : "…"}</div><div className="lbl">Margine mensile medio per cliente{msum ? ` · ${msum.clients} clienti` : ""}</div></div>
        <div className="dash-card"><div className="num" style={{ fontSize: 22 }}>{EUR(stats.ltv_total)}</div><div className="lbl">LTV totale portafoglio clienti</div></div>
        <div className="dash-card" data-testid="ltv-time-card"><div className="num" style={{ fontSize: 22 }}>{stats.avg_client_years} <span style={{ fontSize: 13 }}>anni</span></div><div className="lbl">LTV (durata media rapporto){stats.longest_client_years ? ` · max ${stats.longest_client_years}` : ""}</div></div>
      </div>

      {lossClients.length > 0 && (
        <div className="panel" data-testid="loss-clients-panel" style={{ marginTop: 8, borderLeft: "4px solid var(--danger)" }}>
          <h3 style={{ marginTop: 0 }}>⚠️ Clienti in perdita <span className="nav-badge" style={{ background: "var(--danger)" }}>{lossClients.length}</span></h3>
          <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>Margine annuo negativo nonostante email e incarichi gestiti: assorbono più tempo di quanto fatturano.</p>
          <table>
            <thead><tr>
              <th>Cliente</th><th style={{ textAlign: "right" }}>Email/incarichi</th>
              <th style={{ textAlign: "right" }}>Ore email+incarichi</th>
              <th style={{ textAlign: "right" }}>Ricavo</th><th style={{ textAlign: "right" }}>Margine annuo</th>
            </tr></thead>
            <tbody>
              {lossClients.slice(0, 8).map((c) => (
                <tr key={c.id} data-testid="loss-client-row">
                  <td><Link to={`/aziende/${c.id}`}>{c.name}</Link></td>
                  <td style={{ textAlign: "right" }} className="muted">{c.emails_used || 0} / {c.tasks_used || 0}</td>
                  <td style={{ textAlign: "right" }}>{((c.hours_emails || 0) + (c.hours_tasks || 0)).toLocaleString("it-IT", { maximumFractionDigits: 1 })} h</td>
                  <td style={{ textAlign: "right" }}>{EUR(c.annual_revenue)}</td>
                  <td style={{ textAlign: "right", fontWeight: 700, color: "var(--danger)" }}>{EUR(c.annual_margin)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {lossClients.length > 8 && <Link className="today-link" to="/fatture">Vedi tutti in Fatture → Margini per cliente</Link>}
        </div>
      )}

      {proj && (
        <div className="panel m-o7" data-testid="objective-panel" style={{ marginTop: 8 }}>
          <h3 style={{ marginTop: 0 }}>🎯 Obiettivo {proj.target_year}: superare del {Math.round((proj.growth || 0.30) * 100)}% il {proj.prev_year}</h3>
          <div className="dash-cards" style={{ marginTop: 6 }}>
            <div className="dash-card"><div className="num" style={{ fontSize: 22 }}>{EUR(proj.target_revenue)}</div><div className="lbl">Obiettivo ({proj.prev_year}: {EUR(proj.baseline_revenue)} +{Math.round(proj.growth * 100)}%)</div></div>
            <div className="dash-card"><div className="num" style={{ fontSize: 22 }}>{EUR(proj.secured_revenue)}</div><div className="lbl">Già sicuro (ricorrenti + nuovi incassati + lead chiusi)</div></div>
            <div className="dash-card" data-testid="kpi-won-funnel"><div className="num" style={{ fontSize: 22, color: proj.won_total > 0 ? "var(--green)" : undefined }}>{EUR(proj.won_total || 0)}</div><div className="lbl">{proj.won_total > 0 ? <>🎯 Dai lead chiusi come clienti ({(proj.won_items || []).length})</> : <>🎯 Lead chiusi come clienti (segna "Diventato cliente" + 💶 importi)</>}</div></div>
            <div className="dash-card highlight"><div className="num" style={{ fontSize: 26 }}>{proj.missing_to_target > 0 ? EUR(proj.missing_to_target) : "Obiettivo coperto 🎉"}</div><div className="lbl">{proj.missing_to_target > 0 ? "Ti manca rispetto al garantito (scenario prudente: ricorrenti + incassato dai nuovi)" : "I ricorrenti bastano a centrarlo"}</div></div>
          </div>
          {proj.ytd_compare && (
            <p style={{ margin: "10px 0 2px", fontSize: 14 }} data-testid="dash-ytd-compare">
              📈 Stesso periodo (1/1 → {proj.ytd_compare.as_of}): <strong>{EUR(proj.ytd_compare.current)}</strong> vs {proj.prev_year} <strong>{EUR(proj.ytd_compare.prev)}</strong> →{" "}
              <strong style={{ color: proj.ytd_compare.delta >= 0 ? "var(--green)" : "#fb7185" }}>
                {proj.ytd_compare.delta >= 0 ? "+" : ""}{EUR(proj.ytd_compare.delta)}
                {proj.ytd_compare.delta_pct != null ? ` (${proj.ytd_compare.delta_pct >= 0 ? "+" : ""}${proj.ytd_compare.delta_pct}%)` : ""}
              </strong>
              {" · "}passo per il +{proj.ytd_compare.target_pct}%:{" "}
              {proj.ytd_compare.gap_to_pace > 0
                ? <>mancano <strong style={{ color: "#facc15" }}>{EUR(proj.ytd_compare.gap_to_pace)}</strong></>
                : <strong style={{ color: "var(--green)" }}>superato 🎉</strong>}
            </p>
          )}
          {proj.monthly_compare && proj.monthly_compare.some((x) => x.prev || x.cur) && (() => {
            const max = Math.max(...proj.monthly_compare.map((x) => Math.max(x.prev, x.cur)), 1);
            const ML = ["G", "F", "M", "A", "M", "G", "L", "A", "S", "O", "N", "D"];
            return (
              <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 64, marginTop: 6, maxWidth: 560 }} data-testid="dash-monthly-chart">
                {proj.monthly_compare.map((mc) => (
                  <Link key={mc.m} to={`/fatture?mese=${mc.m}`} data-testid={`dash-month-bar-${mc.m}`}
                    style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 1, minWidth: 0, textDecoration: "none", cursor: "pointer" }}
                    title={`${proj.prev_year}: ${EUR(mc.prev)} · ${proj.target_year}: ${EUR(mc.cur)} — clicca per il dettaglio`}>
                    <div style={{ display: "flex", alignItems: "flex-end", gap: 1, height: 48, width: "100%", justifyContent: "center" }}>
                      <div style={{ width: "40%", height: `${Math.max((mc.prev / max) * 100, mc.prev ? 3 : 0)}%`, background: "#64748b", borderRadius: 1 }} />
                      <div style={{ width: "40%", height: `${Math.max((mc.cur / max) * 100, mc.cur ? 3 : 0)}%`, background: mc.cur >= mc.prev ? "#39ff14" : "#fb7185", borderRadius: 1 }} />
                    </div>
                    <span className="muted" style={{ fontSize: 9 }}>{ML[mc.m - 1]}</span>
                  </Link>
                ))}
              </div>
            );
          })()}
          {(proj.won_items || []).length > 0 && (
            <p className="muted" style={{ fontSize: 12.5, margin: "6px 0 0" }} data-testid="dash-won-items">
              🎯 Nuovi clienti dal funnel: {proj.won_items.map((w) => `${w.name} (${EUR(w.amount)}${w.monthly ? `, ${EUR(w.monthly)}/mese` : ""})`).join(" · ")}
            </p>
          )}
          {obj && (
            <p className="muted" style={{ fontSize: 13, margin: "8px 0 0" }} data-testid="dash-objective-margin">
              🧮 Margine: <strong>{obj.progress_pct}%</strong> dell'obiettivo di periodo (primi {obj.months_elapsed} mesi) · di questo passo a fine anno <strong>{EUR(obj.projected_margin)}</strong>
              {obj.won_revenue_total > 0 && <> · <strong>+{EUR(obj.won_revenue_total)}</strong> dai nuovi clienti chiusi</>}
              {" "}→ {obj.gap_after_won > 0 ? <>mancano <strong>{EUR(obj.gap_after_won)}</strong></> : <strong>✅ obiettivo coperto</strong>}
            </p>
          )}
          <div style={{ height: 10, borderRadius: 6, background: "rgba(255,255,255,.15)", overflow: "hidden", margin: "10px 0 4px" }}>
            <div style={{ height: "100%", width: Math.min(100, Math.round(proj.secured_revenue / proj.target_revenue * 100)) + "%", background: "var(--grad)", borderRadius: 6 }} />
          </div>
          <MilestoneTrack proj={proj} />
          <p className="lbl" style={{ fontSize: 12 }}>
            Considerando i <strong>{proj.counts.recurring} clienti ricorrenti</strong> come già acquisiti. Per coprire il resto: ~<strong>{Math.max(0, Math.ceil(proj.missing_to_target / (proj.avg_revenue_per_client_prev || 1)))} nuovi clienti</strong> oppure recuperare i clienti persi/in calo. <Link to="/fatture" style={{ color: "var(--accent)" }}>Dettaglio proiezione →</Link>
          </p>
        </div>
      )}

      <div className="panel">
        <h3>Distribuzione geografica clienti attivi</h3>
        {stats.geo_distribution && stats.geo_distribution.length > 0 ? (
          <ItalyMap data={stats.geo_distribution} />
        ) : <p className="muted">Nessun cliente attivo con città indicata.</p>}
      </div>

      <div className="panel">
        <h3>Operazioni rapide</h3>
        <div className="toolbar">
          <Link className="btn primary" to="/aziende">+ Nuova azienda</Link>
          <Link className="btn" to="/persone">+ Nuova persona</Link>
          <Link className="btn" to="/import">Importa anagrafiche</Link>
          <Link className="btn" to="/etichette">Gestisci etichette</Link>
        </div>
      </div>

      <div className="panel">
        <h3>In arrivo (prossime fasi)</h3>
        <p className="muted">Template email con campi dinamici, automazioni, funnel, integrazioni Gmail e WhatsApp Business.</p>
      </div>
    </div>
  );
}
